import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { SAGA_ACTIONS } from "src/_config";

interface BillingInfoData {
  name: string;
  phone: string;
  business_name: string;
  address_line_1: string;
  country_id: number;
  city_id: number;
  postal_code: string;
  additional_email: string;
  tax_id: string;
  country?: string;
  city?: string;
  country_details?: {
    id: number;
    name: string;
  };
  city_details?: {
    id: number;
    name: string;
  };
}

interface Country {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
  country_id: number;
}

interface City {
  id: number;
  name: string;
  country_id: number;
}

interface BillingInfoModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: Partial<BillingInfoData>;
  selectedPlan?: {
    id: string;
    name: string;
    price: string;
    period: string;
    description?: string;
  };
  onPaymentModalOpen?: (planData: any) => void;
}

const BillingInfoModal: React.FC<BillingInfoModalProps> = ({
  isVisible,
  onClose,
  onSuccess,
  initialData,
  selectedPlan,
  onPaymentModalOpen,
}) => {
  const dispatch = useDispatch();
  const userApi = useUserApi();
  const commonApi = useCommonApi();
  const toast = useToaster();
  const { t } = useTranslation("payment");
  const userDetails = useAppUserDetailsSelector();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [hasCityInteracted, setHasCityInteracted] = useState(false);
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const [citySelectKey, setCitySelectKey] = useState(0);

  const [formData, setFormData] = useState<BillingInfoData>({
    name: "",
    phone: "",
    business_name: "",
    address_line_1: "",
    country_id: 0,
    city_id: 0,
    postal_code: "",
    additional_email: "",
    tax_id: "",
  });

  // Redux selectors for countries and cities
  const { countriesNew, citiesNew } = useSelector((state: any) => ({
    countriesNew: state.global.countriesNew,
    citiesNew: state.global.citiesNew,
  }));

  // Update countries state when Redux data changes
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id.toString(),
        name: country.name,
      }));
      setCountries(formattedCountries);
      setIsLoadingCountries(false);

      // Set country name if we have a selected country ID
      if (selectedCountryId && !selectedCountryName) {
        const country = countriesNew.find((c: any) => c.id.toString() === selectedCountryId);
        if (country) {
          setSelectedCountryName(country.name);
        }
      }
    }
  }, [countriesNew, selectedCountryId, selectedCountryName]);

  // Update cities state when Redux data changes
  useEffect(() => {
    if (citiesNew && citiesNew.length > 0) {
      const formattedCities = citiesNew.map((city: any) => ({
        id: city.id.toString(),
        name: city.name,
      }));
      setCities(formattedCities);
      setIsLoadingCities(false);

      // Set city name if we have a selected city ID
      if (selectedCityId && !selectedCityName) {
        const city = citiesNew.find((c: any) => c.id.toString() === selectedCityId);
        if (city) {
          setSelectedCityName(city.name);
        }
      }
    }
  }, [citiesNew, selectedCityId, selectedCityName]);

  // Load initial data and fetch country/city data
  useEffect(() => {
    if (isVisible) {
      // Pre-fill with existing billing data if available
      if (initialData) {
        setFormData((prev) => ({
          ...prev,
          ...initialData,
        }));

        // Set selected IDs and names if they exist
        if (initialData.country_id) {
          const countryId = initialData.country_id.toString();
          setSelectedCountryId(countryId);

          // Set country name from initialData if available (from API response with country_details)
          const countryName = (initialData as any).country_details?.name || (initialData as any).country || "";
          if (countryName) {
            setSelectedCountryName(countryName);
          }

          // Only load countries if we don't have them yet
          if (countries.length === 0 && !isLoadingCountries) {
            getCountries();
          }

          // If city_id exists, load cities for that country
          if (initialData.city_id) {
            const cityId = initialData.city_id.toString();
            setSelectedCityId(cityId);

            // Set city name from initialData if available (from API response with city_details)
            const cityName = (initialData as any).city_details?.name || (initialData as any).city || "";
            if (cityName) {
              setSelectedCityName(cityName);
            }

            // Only load cities if we don't have them yet
            if (cities.length === 0 && !isLoadingCities) {
              getCities(countryId);
            }
          }
        }
      } else if (userDetails) {
        // Pre-fill form with user details if no billing data exists
        const userData = userDetails?.user;
        const companyData = userDetails?.company;

        // Build name from user data
        let userName = "";
        if (userData?.first_name && userData?.last_name) {
          userName = `${userData.first_name} ${userData.last_name}`;
        } else if (userData?.first_name) {
          userName = userData.first_name;
        } else if (companyData?.name) {
          userName = companyData.name;
        }

        // Get phone number from user data (only the mobile number, not phone code)
        let userPhone = "";
        if (userData?.mobile_number) {
          userPhone = userData.mobile_number.toString();
        }

        // Get email from user data
        const userEmail = userData?.email || "";

        // Get company name
        const businessName = companyData?.name || "";

        setFormData((prev) => ({
          ...prev,
          name: userName || prev.name,
          phone: userPhone || prev.phone,
          additional_email: userEmail || prev.additional_email,
          business_name: businessName || prev.business_name,
        }));

        // If company has location data, pre-fill it
        if (companyData?.location) {
          const countryId = companyData.location.toString();
          setSelectedCountryId(countryId);

          // Set country name if available
          if (companyData?.country_details?.name) {
            setSelectedCountryName(companyData.country_details.name);
          }

          // Only load countries if we don't have them yet
          if (countries.length === 0 && !isLoadingCountries) {
            getCountries();
          }

          // If city exists, load cities for that country
          if (companyData?.city) {
            const cityId = companyData.city.toString();
            setSelectedCityId(cityId);

            // Set city name if available
            if (companyData?.city_details?.name) {
              setSelectedCityName(companyData.city_details.name);
            }

            // Only load cities if we don't have them yet
            if (cities.length === 0 && !isLoadingCities) {
              getCities(countryId);
            }
          }
        }
      }
    }
  }, [initialData, isVisible, userDetails]);

  // Reset interaction flags when modal is closed to ensure proper behavior on next open
  useEffect(() => {
    if (!isVisible) {
      setHasCountryInteracted(false);
      setHasCityInteracted(false);
    }
  }, [isVisible]);

  // Sync country_id in formData when selectedCountryId changes
  useEffect(() => {
    if (selectedCountryId) {
      setFormData((prev) => ({
        ...prev,
        country_id: parseInt(selectedCountryId),
      }));
    }
  }, [selectedCountryId]);

  // Sync city_id in formData when selectedCityId changes
  useEffect(() => {
    if (selectedCityId) {
      setFormData((prev) => ({
        ...prev,
        city_id: parseInt(selectedCityId),
      }));
    }
  }, [selectedCityId]);

  // Fetch countries from API
  const getCountries = (searchName: string = "") => {
    if (isLoadingCountries) return; // Prevent duplicate calls
    setIsLoadingCountries(true);
    const params: any = { limit: 30 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Fetch cities from API based on country
  const getCities = (countryId: string, searchName: string = "") => {
    if (!countryId) {
      setCities([]);
      setIsLoadingCities(false);
      return;
    }
    if (isLoadingCities) return; // Prevent duplicate calls
    setIsLoadingCities(true);
    const params: any = { country_id: countryId, limit: 200 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
    });
  };

  // Handle country selection
  const handleCountrySelect = (item: any) => {
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    setFormData((prev) => ({ ...prev, country_id: parseInt(item.id), city_id: 0 }));

    // Clear city when country changes
    setSelectedCityId("");
    setSelectedCityName("");
    setCities([]);

    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    getCities(item.id); // Load cities for selected country
  };

  // Handle city selection
  const handleCitySelect = (item: any) => {
    setSelectedCityId(item.id);
    setSelectedCityName(item.name);
    setFormData((prev) => ({ ...prev, city_id: parseInt(item.id) }));

    // Reset interaction and remount to close dropdown
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Handle phone number formatting
      const numericValue = value.replace(/\D/g, ""); // Remove non-numeric characters

      // Limit to 10 digits
      if (numericValue.length <= 10) {
        // Format as (XXX) XXX-XXXX
        let formattedValue = "";
        if (numericValue.length > 0) {
          if (numericValue.length <= 3) {
            formattedValue = `(${numericValue}`;
          } else if (numericValue.length <= 6) {
            formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
          } else {
            formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
          }
        }

        setFormData((prev) => ({
          ...prev,
          [name]: formattedValue,
        }));
      }
      return;
    }

    // Convert field to number only for country_id and city_id, keep other fields as strings
    const numericValue = name === "country_id" || name === "city_id" ? parseInt(value) || 0 : value;

    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!userDetails?.user) {
      toast.error(t("billingInfo.validation.loginRequired"));
      return;
    }

    // Validate phone number if provided
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, "");
      if (phoneDigits.length !== 10) {
        toast.error(t("billingInfo.validation.invalidPhone"));
        return;
      }
    }

    // Validate email format if provided
    if (formData.additional_email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.additional_email)) {
        toast.error(t("billingInfo.validation.invalidEmail"));
        return;
      }
    }

    // Validate required location fields
    if (!selectedCountryId || formData.country_id === 0) {
      toast.error(t("billingInfo.validation.selectCountry"));
      return;
    }
    if (!selectedCityId || formData.city_id === 0) {
      toast.error(t("billingInfo.validation.selectCity"));
      return;
    }

    // Validate Tax ID for UAE
    if (selectedCountryName.toLowerCase() === "uae" && (!formData.tax_id || !String(formData.tax_id).trim())) {
      toast.error(t("billingInfo.validation.taxIdRequired"));
      return;
    }

    setIsLoading(true);

    try {
      // Prepare data for API - extract only digits from phone number
      const apiData = {
        ...formData,
        phone: formData.phone ? formData.phone.replace(/\D/g, "") : formData.phone,
        tax_id: selectedCountryName.toLowerCase() === "uae" ? formData.tax_id || "" : "",
      };

      // Call the add-company-billing-info API
      userApi.callAddCompanyBillingInfo(
        apiData,
        (message: string, response: any) => {
          console.log("Billing info saved successfully:", response);
          toast.success(message || t("billingInfo.validation.saveSuccess"));
          setIsLoading(false);

          // If there's a selected plan, trigger payment modal
          if (selectedPlan && onPaymentModalOpen) {
            onPaymentModalOpen(selectedPlan);
          } else if (onSuccess) {
            onSuccess();
          } else {
            onClose();
          }
        },
        (error: any) => {
          console.error("Failed to save billing info:", error);
          setIsLoading(false);

          // Handle specific error cases
          if (error && error.status === 401) {
            toast.error(t("billingInfo.validation.authFailed"));
          } else if (error && error.message) {
            toast.error(error.message);
          } else {
            toast.error(t("billingInfo.validation.saveFailed"));
          }
        }
      );
    } catch (error) {
      console.error("Error submitting billing info:", error);
      toast.error(t("billingInfo.validation.saveError"));
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upgrade-plan-modal billing-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">{t("billingInfo.title")}</h2>
            <p className="modal-subtitle">{t("billingInfo.subtitle")}</p>
          </div>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-content billing-form-content">
          <div className="billing-form-grid">
            <div className="billing-form-group">
              <label htmlFor="name" className="billing-form-label">
                {t("billingInfo.fields.fullName")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="billing-form-input"
              />
            </div>
            <div className="billing-form-group">
              <label htmlFor="phone" className="billing-form-label">
                {t("billingInfo.fields.phone")}
                <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(123) 456-7890"
                maxLength={14}
                className="billing-form-input"
              />
            </div>
          </div>

          <div className="billing-form-grid">
            <div className="billing-form-group">
              <label htmlFor="business_name" className="billing-form-label">
                {t("billingInfo.fields.businessName")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                required
                className="billing-form-input"
              />
            </div>
            <div className="billing-form-group">
              <label htmlFor="additional_email" className="billing-form-label">
                {t("billingInfo.fields.additionalEmail")} <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="additional_email"
                name="additional_email"
                value={formData.additional_email}
                onChange={handleInputChange}
                className="billing-form-input"
                required
              />
            </div>
          </div>

          <div className="billing-form-full-width">
            <label htmlFor="address_line_1" className="billing-form-label">
              {t("billingInfo.fields.address")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="address_line_1"
              name="address_line_1"
              value={formData.address_line_1}
              onChange={handleInputChange}
              required
              className="billing-form-input"
            />
          </div>

          <div className="billing-form-grid">
            <div className="billing-form-group">
              <label htmlFor="country_id" className="billing-form-label">
                {t("billingInfo.fields.country")} <span className="text-danger">*</span>
              </label>
              <div className="autocomplete-container">
                <ReactSearchAutocomplete
                  key={countrySelectKey}
                  items={hasCountryInteracted ? countries : []}
                  onSearch={(string, results) => {
                    setHasCountryInteracted(true);
                    if (string.length > 0) {
                      getCountries(string);
                    } else {
                      getCountries();
                    }
                  }}
                  onFocus={() => {
                    if (!hasCountryInteracted) {
                      setHasCountryInteracted(true);
                      getCountries();
                    }
                  }}
                  onSelect={(item) => handleCountrySelect(item)}
                  onClear={() => {
                    setSelectedCountryId("");
                    setSelectedCountryName("");
                    setSelectedCityId("");
                    setSelectedCityName("");
                    setFormData((prev) => ({ ...prev, country_id: 0, city_id: 0 }));
                    setCities([]);
                    setHasCountryInteracted(false);
                    setCountrySelectKey((k) => k + 1);
                  }}
                  placeholder={t("billingInfo.fields.selectCountry")}
                  inputSearchString={selectedCountryName || ""}
                  showIcon={false}
                  showNoResults={false}
                  autoFocus={false}
                  styling={{
                    height: "45px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    boxShadow: "none",
                    hoverBackgroundColor: "#f3f4f6",
                    color: "#374151",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    iconColor: "#6b7280",
                    lineColor: "#e5e7eb",
                    placeholderColor: "#9ca3af",
                    clearIconMargin: "3px 8px 0 0",
                    zIndex: 2,
                  }}
                />
              </div>
            </div>
            <div className="billing-form-group">
              <label htmlFor="city_id" className="billing-form-label">
                {t("billingInfo.fields.city")} <span className="text-danger">*</span>
              </label>
              <div className="autocomplete-container">
                <ReactSearchAutocomplete
                  key={citySelectKey}
                  items={hasCityInteracted ? cities : []}
                  onSearch={(string, results) => {
                    setHasCityInteracted(true);
                    if (selectedCountryId) {
                      if (string.length > 0) {
                        getCities(selectedCountryId, string);
                      } else {
                        getCities(selectedCountryId);
                      }
                    }
                  }}
                  onFocus={() => {
                    if (!hasCityInteracted && selectedCountryId) {
                      setHasCityInteracted(true);
                      getCities(selectedCountryId);
                    }
                  }}
                  onSelect={(item) => handleCitySelect(item)}
                  onClear={() => {
                    setSelectedCityId("");
                    setSelectedCityName("");
                    setFormData((prev) => ({ ...prev, city_id: 0 }));
                    setHasCityInteracted(false);
                    setCitySelectKey((k) => k + 1);
                  }}
                  placeholder={
                    !selectedCountryId ? t("billingInfo.fields.selectCountryFirst") : t("billingInfo.fields.selectCity")
                  }
                  inputSearchString={selectedCityName || ""}
                  showIcon={false}
                  showNoResults={false}
                  autoFocus={false}
                  styling={{
                    height: "45px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: !selectedCountryId ? "#f3f4f6" : "white",
                    boxShadow: "none",
                    hoverBackgroundColor: "#f3f4f6",
                    color: "#374151",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    iconColor: "#6b7280",
                    lineColor: "#e5e7eb",
                    placeholderColor: "#9ca3af",
                    clearIconMargin: "3px 8px 0 0",
                    zIndex: 2,
                  }}
                />
              </div>
            </div>
          </div>

          {selectedCountryName.toLowerCase() === "uae" && (
            <div className="billing-form-grid">
              <div className="billing-form-group">
                <label htmlFor="tax_id" className="billing-form-label">
                  {t("billingInfo.fields.taxId")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="tax_id"
                  name="tax_id"
                  value={formData.tax_id}
                  onChange={handleInputChange}
                  className="billing-form-input"
                />
                <small className="text-muted" style={{ display: "block", marginTop: "5px", fontSize: "13px" }}>
                  <i className="fa fa-info-circle"></i> {t("billingInfo.fields.vatNote")}
                </small>
              </div>
              <div className="billing-form-group">{/* Empty div for grid layout */}</div>
            </div>
          )}

          <div className="billing-form-grid billing-form-last-row">
            <div className="billing-form-group">
              <label htmlFor="postal_code" className="billing-form-label">
                {t("billingInfo.fields.postalCode")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                required
                className="billing-form-input"
              />
            </div>
            <div className="billing-form-group">{/* Empty div for grid layout */}</div>
          </div>

          <div className="billing-form-actions">
            <button type="button" onClick={onClose} disabled={isLoading} className="btn-cancel">
              {t("billingInfo.buttons.cancel")}
            </button>
            <button type="submit" disabled={isLoading} className="btn-preview">
              {isLoading ? t("billingInfo.buttons.saving") : t("billingInfo.buttons.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingInfoModal;
